import React from 'react';
import { Segment, Header, List } from 'semantic-ui-react';
import { Helmet } from 'react-helmet';
import { HashLink as Link } from 'react-router-hash-link';
import PageContainer from '../PageContainer';


const Policies = () => (
  <PageContainer>
    <Helmet>
      <title>Policies - AssistList</title>
    </Helmet>
    <Segment>
      <Header as="h1" id="policies-top" content="Policies" />
      <p>Last updated: September 6, 2026</p>
      <p>
        AssistList is a volunteer-run registered Canadian charity committed to providing an
        accessible and trusted online marketplace for previously owned home health medical
        equipment across British Columbia.
      </p>
      <p>
        These policies explain how our marketplace operates, the responsibilities of our users, and
        how we collect, use and protect personal information. Together, they help us maintain a
        safe, fair and welcoming community while supporting AssistList&apos;s charitable mission.
      </p>
      <List bulleted>
        <List.Item>
          <a href="#terms-of-use">Terms of Use</a>
        </List.Item>
        <List.Item>
          <a href="#marketplace-rules">Marketplace Rules</a>
        </List.Item>
        <List.Item>
          <a href="#privacy-policy">Privacy Policy</a>
        </List.Item>
      </List>
      <p>
        If you have any questions about these policies, please see the&nbsp;
        <a href="#contact-us">Contact Us</a> section at the bottom of this page.
      </p>
    </Segment>

    <Segment>
      <Header as="h1" id="terms-of-use" content="Terms of Use" />
      <p>
        These Terms of Use (&quot;Terms&quot;) govern your access to and use of AssistList.ca, our
        website, applications, services, and related content (collectively, the
        &quot;Services&quot;).
      </p>
      <p>
        By accessing or using the Services, you agree to be bound by these Terms, the&nbsp;
        <a href="#marketplace-rules">Marketplace Rules</a>, the&nbsp;
        <a href="#privacy-policy">Privacy Policy</a> and any other policies referenced on our
        website. If you do not agree to these Terms, please do not use the Services.
      </p>
      <p>
        We may revise these Terms from time to time. The most current version will always be posted
        on our website. By continuing to use the Services after changes are posted, you agree to the
        revised Terms.
      </p>

      <Header as="h3" content="1. Our Mission" />
      <p>
        AssistList promotes public health by improving access to affordable home health medical
        equipment across British Columbia.
      </p>
      <p>
        As a volunteer-run charity, we strive to strengthen communities by helping individuals
        safely exchange previously owned equipment, reducing waste, improving accessibility and
        supporting independence.
      </p>
      <p>
        We ask all users to participate honestly, respectfully and in a manner consistent with our
        charitable mission.
      </p>
      <p>
        AssistList reserves the right to take reasonable action regarding listings and accounts that
        violate these Terms or the Marketplace Rules, or are inconsistent with the charitable
        purpose of AssistList.
      </p>

      <Header as="h3" content="2. Eligibility" />
      <p>By using AssistList, you confirm that:</p>
      <List bulleted>
        <List.Item>
          You are legally able to enter into these Terms, or, if you are under 19 years old, you
          have obtained permission from a parent or legal guardian to use AssistList;
        </List.Item>
        <List.Item>
          Any information you provide is accurate and up to date; and
        </List.Item>
        <List.Item>
          You will comply with all applicable laws while using the Services.
        </List.Item>
      </List>
      <p>
        If you are using AssistList on behalf of an organization or business, you confirm that you
        are authorized to act on its behalf and that the organization agrees to these Terms.
      </p>

      <Header as="h3" content="3. Your Account" />
      <p>Certain features of AssistList require you to create an account.</p>
      <p>
        You are responsible for maintaining the security of your account and login credentials, and
        for all activity conducted through your account.
      </p>
      <p>You agree to:</p>
      <List bulleted>
        <List.Item>
          Provide accurate and truthful information.
        </List.Item>
        <List.Item>
          Notify us promptly if you believe your account has been compromised.
        </List.Item>
        <List.Item>
          Comply with these Terms and the Marketplace Rules.
        </List.Item>
      </List>
      <p>
        AssistList may suspend or terminate accounts that contain false information, impersonate
        another person or organization, or otherwise violate these Terms.
      </p>

      <Header as="h3" content="4. License to Use AssistList" />
      <p>
        Subject to these Terms, AssistList grants you a limited, non-exclusive, non-transferable and
        revocable license to access and use the Services for their intended purpose.
      </p>
      <p>
        You retain ownership of the content you post, including listings, photographs and
        descriptions.
      </p>
      <p>
        By submitting content to AssistList, you grant us a worldwide, royalty-free license to
        display, reproduce, modify, distribute and use that content as reasonably necessary to
        operate, improve, promote and protect the Services.
      </p>
      <p>
        This license ends when your content is permanently removed from our systems, except where
        retention is required by law or for legitimate operational, security or legal purposes.
      </p>

      <Header as="h3" content="5. Acceptable Use" />
      <p>
        You agree to use AssistList honestly, respectfully and in accordance with these Terms and
        the Marketplace Rules.
      </p>
      <p>You must not:</p>
      <List bulleted>
        <List.Item>
          Violate any applicable law.
        </List.Item>
        <List.Item>
          Interfere with the operation, security or availability of the Services.
        </List.Item>
        <List.Item>
          Upload malware, viruses, malicious code or other harmful software.
        </List.Item>
        <List.Item>
          Use automated software, bots, crawlers, scrapers or similar technologies without our
          written permission.
        </List.Item>
        <List.Item>
          Attempt to gain unauthorized access to AssistList, its systems or another user&apos;s
          account.
        </List.Item>
      </List>
      <p>
        Additional rules governing listings, marketplace conduct, commercial use, moderation and
        prohibited activities are set out in the&nbsp;
        <a href="#marketplace-rules">Marketplace Rules</a>, which form part of these Terms.
      </p>

      <Header as="h3" content="6. Moderation" />
      <p>
        To protect our community, volunteers, and the integrity of the marketplace, AssistList may
        review, restrict, remove, or refuse listings and accounts that violate these Terms or the
        Marketplace Rules, or are inconsistent with AssistList&apos;s charitable mission.
      </p>
      <p>
        AssistList may use automated systems and manual review to identify fraud, spam, duplicate
        content, suspicious activity, policy violations, and other behaviour that may affect the
        safety or integrity of the marketplace.
      </p>
      <p>Depending on the circumstances, AssistList may:</p>
      <List bulleted>
        <List.Item>
          Remove or refuse listings;
        </List.Item>
        <List.Item>
          Request additional information or verification;
        </List.Item>
        <List.Item>
          Restrict marketplace features;
        </List.Item>
        <List.Item>
          Suspend or terminate accounts;
        </List.Item>
        <List.Item>
          Merge duplicate accounts;
        </List.Item>
        <List.Item>
          Refuse future registrations; or
        </List.Item>
        <List.Item>
          Take other reasonable measures to protect the Services and community.
        </List.Item>
      </List>
      <p>
        These actions may occur with or without prior notice where necessary to protect the
        integrity of the marketplace.
      </p>

      <Header as="h3" content="7. Disclaimer of Warranties" />
      <p>
        AssistList is provided on an &quot;as is&quot; and &quot;as available&quot; basis.
      </p>
      <p>
        AssistList does not provide medical, clinical, or health advice, nor do we prescribe
        equipment. We recommend consulting a qualified healthcare professional (such as an
        occupational therapist or physiotherapist) before acquiring or using specialized home health
        medical equipment.
      </p>
      <p>
        AssistList does not process payments, hold funds, or act as a payment mediator between
        users. All financial transactions, donations, or equipment exchanges are conducted directly
        between users at their own risk.
      </p>
      <p>
        While we strive to maintain a safe and reliable marketplace, we do not guarantee that:
      </p>
      <List bulleted>
        <List.Item>
          The Services will always be available, uninterrupted, secure, or error-free.
        </List.Item>
        <List.Item>
          Listings are accurate, complete, or up to date.
        </List.Item>
        <List.Item>
          Listed items are available, safe, authentic, or suitable for your intended use.
        </List.Item>
        <List.Item>
          Users will act honestly or complete transactions.
        </List.Item>
      </List>
      <p>
        AssistList does not inspect, verify, or endorse items or users unless expressly stated.
      </p>
      <p>
        Users are responsible for exercising their own judgment and taking appropriate precautions
        when communicating with others, arranging exchanges, or purchasing or accepting equipment.
      </p>

      <Header as="h3" content="8. Limitation of Liability" />
      <p>
        AssistList provides a platform that enables users to connect with one another. We are not a
        party to transactions between users and are not responsible for the actions, omissions, or
        conduct of buyers, sellers, donors, or recipients.
      </p>
      <p>
        To the fullest extent permitted by law, AssistList, its directors, officers, volunteers,
        employees and representatives are not liable for any direct, indirect, incidental,
        consequential, special, or punitive damages arising from or relating to:
      </p>
      <List bulleted>
        <List.Item>
          Your use of, or inability to use, the Services.
        </List.Item>
        <List.Item>
          Transactions or communications between users.
        </List.Item>
        <List.Item>
          The condition, quality, safety, legality, or suitability of listed items.
        </List.Item>
        <List.Item>
          Inaccurate or misleading listings.
        </List.Item>
        <List.Item>
          Unauthorized access to your account or information.
        </List.Item>
        <List.Item>
          Interruptions, errors, or technical issues affecting the Services.
        </List.Item>
      </List>
      <p>
        Nothing in these Terms excludes liability that cannot be excluded under applicable law.
      </p>

      <Header as="h3" content="9. Indemnification" />
      <p>
        You agree to indemnify and hold harmless AssistList Association, its directors, officers,
        volunteers, employees and representatives from any claims, liabilities, damages, losses,
        costs, or expenses (including reasonable legal fees) arising from:
      </p>
      <List bulleted>
        <List.Item>
          Your use of the Services.
        </List.Item>
        <List.Item>
          Your listings or other content you submit.
        </List.Item>
        <List.Item>
          Your violation of these Terms, the Marketplace Rules, or applicable law.
        </List.Item>
        <List.Item>
          Your infringement of another person&apos;s rights.
        </List.Item>
      </List>

      <Header as="h3" content="10. Governing Law" />
      <p>
        These Terms are governed by the laws of the Province of British Columbia and the applicable
        federal laws of Canada.
      </p>
      <p>
        Any disputes arising from or relating to these Terms or your use of the Services will be
        subject to the exclusive jurisdiction of the courts of British Columbia, unless applicable
        law provides otherwise.
      </p>
      <p>
        If any provision of these Terms is found to be invalid or unenforceable, the remaining
        provisions will continue in full force and effect.
      </p>

      <Header as="h3" content="11. Entire Agreement" />
      <p>
        These Terms, together with the Marketplace Rules, Privacy Policy and any other policies
        expressly incorporated by reference, constitute the entire agreement between you and
        AssistList regarding your use of the Services.
      </p>
      <p>
        <a href="#policies-top">Back to top</a>
      </p>
    </Segment>

    <Segment>
      <Header as="h1" id="marketplace-rules" content="Marketplace Rules" />

      <Header as="h3" content="1. Purpose" />
      <p>
        AssistList is a volunteer-run charitable marketplace that connects people across British
        Columbia with previously owned home health medical equipment.
      </p>
      <p>
        These Marketplace Rules help ensure the platform remains safe, fair and focused on serving
        our community. By using AssistList, you agree to follow these rules. AssistList may take
        action against listings or accounts that violate these Marketplace Rules, as described in
        our Terms of Use.
      </p>
      <p>
        For guidance on conducting safe email communications and equipment exchanges, please review
        the Safety section of our&nbsp;
        <Link to="/resources#faq-safety">Frequently Asked Questions</Link> page.
      </p>

      <Header as="h3" content="2. Eligible Equipment" />
      <p>Only home health medical equipment may be listed on AssistList.</p>
      <p>
        Listings may be removed if items fall outside AssistList&apos;s scope or cannot be safely
        exchanged through the marketplace. Please review our&nbsp;
        <a
          href="https://docs.google.com/spreadsheets/d/1YTQubUnhOFs6djc_ON3InWNdgZA-NkEYdMwWCDkzq9w/edit?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
        >
          list of items not accepted
        </a>.
      </p>

      <Header as="h3" content="3. Your Listings" />
      <p>When creating a listing, you agree to:</p>
      <List bulleted>
        <List.Item>
          Accurately describe the item and its condition.
        </List.Item>
        <List.Item>
          Upload photographs that represent the actual item being offered.
        </List.Item>
        <List.Item>
          Select the most appropriate category.
        </List.Item>
        <List.Item>
          List only items that you have the authority to sell or donate.
        </List.Item>
        <List.Item>
          Update or remove your listing once the item is no longer available.
        </List.Item>
        <List.Item>
          Respond to interested buyers or recipients in a timely manner whenever possible.
        </List.Item>
      </List>

      <Header as="h3" content="4. Prohibited Listings" />
      <p>You may not:</p>
      <List bulleted>
        <List.Item>
          List&nbsp;
          <a
            href="https://docs.google.com/spreadsheets/d/1YTQubUnhOFs6djc_ON3InWNdgZA-NkEYdMwWCDkzq9w/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
          >
            items that are not accepted by AssistList
          </a>.
        </List.Item>
        <List.Item>
          List duplicate listings for the same item.
        </List.Item>
        <List.Item>
          List misleading, deceptive, or fraudulent listings.
        </List.Item>
        <List.Item>
          Use stock photos, AI-generated images or images that do not accurately represent the item
          being offered.
        </List.Item>
        <List.Item>
          List items, photographs, descriptions, or other content that infringe another
          person&apos;s copyright or other intellectual property rights.
        </List.Item>
        <List.Item>
          List items that are illegal to possess or transfer.
        </List.Item>
        <List.Item>
          List items located outside British Columbia.
        </List.Item>
      </List>

      <Header as="h3" content="5. Marketplace Conduct" />
      <p>Users are expected to interact respectfully and honestly.</p>
      <p>You may not:</p>
      <List bulleted>
        <List.Item>
          Misrepresent your identity or affiliation.
        </List.Item>
        <List.Item>
          Misrepresent the ownership, location, or condition of an item.
        </List.Item>
        <List.Item>
          Send spam, unsolicited messages, or commercial solicitations (including marketing
          competing services) through seller messaging or contact forms, which violates
          Canada&apos;s Anti-Spam Legislation (CASL).
        </List.Item>
        <List.Item>
          Harass, threaten, intimidate, or abuse other users.
        </List.Item>
        <List.Item>
          Attempt to obtain personal or financial information unrelated to completing a transaction.
        </List.Item>
      </List>
      <p>
        Users are solely responsible for thoroughly cleaning, sanitizing, inspecting, and verifying
        the physical condition and mechanical safety of any equipment before listing, transferring
        or using it.
      </p>

      <Header as="h3" content="6. Fair Marketplace Use" />
      <p>
        To ensure everyone has a fair opportunity to buy, sell and donate equipment, users may not:
      </p>
      <List bulleted>
        <List.Item>
          Create multiple accounts to gain an unfair advantage.
        </List.Item>
        <List.Item>
          Repeatedly renew, recreate, duplicate or repost listings, or otherwise unfairly increase
          their visibility.
        </List.Item>
        <List.Item>
          Manipulate search results, listing order or marketplace visibility.
        </List.Item>
        <List.Item>
          Circumvent moderation decisions.
        </List.Item>
        <List.Item>
          Interfere with the operation of the marketplace.
        </List.Item>
      </List>

      <Header as="h3" content="7. Commercial Use" />
      <p>
        AssistList exists to support individuals and communities by facilitating the exchange of
        home health medical equipment. Businesses and organizations may use AssistList only with
        prior written authorization from AssistList.
      </p>
      <p>You must not:</p>
      <List bulleted>
        <List.Item>
          Use AssistList primarily to buy, sell, or acquire equipment inventory as part of a
          commercial business without our prior written authorization;
        </List.Item>
        <List.Item>
          Represent a business or organization through a personal account without authorization;
        </List.Item>
        <List.Item>
          Misrepresent the purpose or intended recipient of an item in order to access the
          marketplace;
        </List.Item>
        <List.Item>
          Use the Services in a manner that is inconsistent with AssistList&apos;s charitable
          mission.
        </List.Item>
      </List>

      <Header as="h3" content="8. Release of Claims" />
      <p>
        You release AssistList, its directors, officers, volunteers, employees, and representatives
        from any dispute, harm, or damages arising out of transactions or interactions with other
        marketplace users.
      </p>

      <Header as="h3" content="9. Reporting Concerns" />
      <p>
        If you believe a listing violates these Marketplace Rules or appears fraudulent, please
        report it using the tools available on AssistList or contact us at:&nbsp;
        <a href="mailto:support@assistlist.ca">support@assistlist.ca</a>
      </p>
      <p>
        <a href="#policies-top">Back to top</a>
      </p>
    </Segment>

    <Segment>
      <Header as="h1" id="privacy-policy" content="Privacy Policy" />

      <Header as="h3" content="1. Our Commitment to Your Privacy" />
      <p>
        AssistList Association (&quot;AssistList&quot;, &quot;we&quot;, &quot;our&quot;, or
        &quot;us&quot;) is committed to protecting your privacy.
      </p>
      <p>
        This Privacy Policy explains what personal information we collect, how we use it, who we
        share it with, and the choices you have regarding your information when you use
        AssistList.ca and our related services (collectively, the &quot;Services&quot;).
      </p>
      <p>
        We collect, use and disclose personal information in accordance with British
        Columbia&apos;s Personal Information Protection Act (PIPA) and other applicable privacy
        laws, as described in this Privacy Policy.
      </p>

      <Header as="h3" content="2. Information We Collect" />
      <p>
        We collect only the information necessary to operate AssistList and provide our Services.
      </p>
      <p>Depending on how you use AssistList, the information you provide may include:</p>
      <List bulleted>
        <List.Item>
          Your email address and account authentication information.
        </List.Item>
        <List.Item>
          Listings that you create, including published listings and saved drafts.
        </List.Item>
        <List.Item>
          Information about the status of your listings, such as when you mark an item as sold or
          remove a listing.
        </List.Item>
        <List.Item>
          The first message you send to a seller through our contact form.
        </List.Item>
        <List.Item>
          Information you voluntarily provide when contacting us via email or the Contact Form on
          our website.
        </List.Item>
      </List>

      <Header as="h3" content="3. Information Collected Automatically" />
      <p>
        When you use AssistList, we and our service providers may automatically collect certain
        technical and usage information, such as your IP address, browser and device information,
        pages viewed, interactions with the website, and information collected through cookies or
        similar technologies.
      </p>
      <p>
        We use this information to operate and secure the website, understand how people use
        AssistList, improve our Services, detect and prevent fraud or abuse, and measure the
        effectiveness of our website and outreach activities.
      </p>
      <p>
        Most web browsers allow you to control or disable cookies through your browser settings. You
        may disable cookies; however, some features of AssistList may not function properly.
      </p>

      <Header as="h3" content="4. Third-Party Service Providers" />
      <p>
        We use trusted third-party service providers to help operate, maintain, secure and improve
        AssistList.
      </p>
      <List bulleted>
        <List.Item>
          <strong>Transactional email services</strong>: We use Mailgun to send transactional emails
          related to your account and listings, such as alerts about new or matching listings
          you&apos;ve subscribed to receive. If you subscribe to listing alerts, you can unsubscribe
          at any time using the link included in each alert email, or by managing your alert
          preferences in your account.
        </List.Item>
        <List.Item>
          <strong>Email newsletter services</strong>: We use Mailchimp to send newsletters and
          community updates to users who subscribe. If you subscribe, your email address is stored
          and processed by Mailchimp on our behalf. You can unsubscribe at any time using the link
          included in every newsletter email.
        </List.Item>
        <List.Item>
          <strong>Analytics and website improvement services:</strong> We use services such as
          Google Analytics and Hotjar to understand how visitors use AssistList, identify
          opportunities to improve the website, troubleshoot issues, and gather feedback. These
          services may use cookies or similar technologies and may collect information about website
          visits, device and browser characteristics, and interactions with the website. You can opt
          out of Google Analytics using the&nbsp;
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            Google Analytics Opt-out Browser Add-on
          </a>.
        </List.Item>
        <List.Item>
          <strong>Google advertising services:</strong> We use Google Ads to promote AssistList on
          third-party websites and search results and to measure the effectiveness of our
          advertising, including through conversion tracking. Google may collect information about
          interactions with our advertisements and activity resulting from those advertisements. You
          can manage or opt out of personalized advertising through&nbsp;
          <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>.
        </List.Item>
        <List.Item>
          <strong>Amazon Web Services (AWS):</strong> We use AWS to host and operate parts of our
          Services and store information necessary to provide the Services.
        </List.Item>
      </List>
      <p>
        These providers may process information in accordance with their own privacy policies and
        terms. We take reasonable steps to use service providers that provide appropriate safeguards
        for information handled on our behalf. Some of our service providers may process or store
        information outside Canada. Where this occurs, we take reasonable steps to protect personal
        information in accordance with applicable privacy requirements. Personal information stored
        or processed outside Canada may be subject to foreign laws and accessible to local law
        enforcement, courts, or regulatory authorities.
      </p>
      <p>We may change or add service providers from time to time as our Services evolve.</p>

      <Header as="h3" content="5. How We Use Your Information" />
      <p>We use your information to:</p>
      <List bulleted>
        <List.Item>
          Provide and maintain AssistList.
        </List.Item>
        <List.Item>
          Create and manage your account.
        </List.Item>
        <List.Item>
          Publish and manage your listings.
        </List.Item>
        <List.Item>
          Facilitate communication between buyers and sellers.
        </List.Item>
        <List.Item>
          Provide customer support.
        </List.Item>
        <List.Item>
          Improve the functionality, accessibility and performance of the Services.
        </List.Item>
        <List.Item>
          Detect fraud, spam, duplicate accounts, suspicious activity and other policy violations.
        </List.Item>
        <List.Item>
          Measure website usage and improve user experience.
        </List.Item>
        <List.Item>
          Comply with legal obligations.
        </List.Item>
      </List>
      <p>
        If you subscribe to our newsletter, we will send non-essential communications (such as
        community updates and newsletters) via Mailchimp with your consent, and you may unsubscribe
        at any time using the link in any newsletter email or by contacting us.
      </p>
      <p>
        AssistList may use automated technologies, including artificial intelligence, to help
        identify fraudulent or suspicious activity and improve the safety of the marketplace.
      </p>

      <Header as="h3" content="6. Sharing Your Information" />
      <p>
        Listings that you choose to publish are publicly visible. When you contact a seller through
        AssistList, your email address and message are shared with that seller so they can respond
        to your inquiry.
      </p>
      <p>We may share information:</p>
      <List bulleted>
        <List.Item>
          With service providers that help us operate AssistList.
        </List.Item>
        <List.Item>
          When required by law, court order, or legal process.
        </List.Item>
        <List.Item>
          To protect the rights, safety, or property of AssistList, our users, volunteers, or the
          public.
        </List.Item>
        <List.Item>
          In connection with a merger, reorganization, or transfer of AssistList&apos;s assets.
        </List.Item>
        <List.Item>
          With your consent.
        </List.Item>
      </List>
      <p>We do not sell your personal information.</p>

      <Header as="h3" content="7. Data Retention" />
      <p>
        We retain personal information only for as long as reasonably necessary to fulfill the
        purposes for which it was collected, operate and protect AssistList, resolve disputes,
        comply with legal obligations, and maintain appropriate business and security records.
      </p>
      <p>
        When personal information is no longer required for these purposes, we take reasonable steps
        to securely delete or anonymize it. Some information may remain in backups or system logs
        for a limited period after it has been deleted from active systems.
      </p>

      <Header as="h3" content="8. Protecting Your Information" />
      <p>
        We use reasonable administrative, technical and organizational safeguards to protect the
        information we collect. However, no method of transmitting or storing information over the
        Internet is completely secure and we cannot guarantee absolute security.
      </p>
      <p>
        We limit access to personal information to authorized individuals who require it to perform
        their responsibilities for AssistList.
      </p>

      <Header as="h3" content="9. Your Choices" />
      <p>You may at any time:</p>
      <List bulleted>
        <List.Item>
          Create, update and delete your listings through your account.
        </List.Item>
        <List.Item>
          Subscribe and unsubscribe to listing alert emails and our newsletter.
        </List.Item>
        <List.Item>
          Request deletion of your AssistList account by contacting&nbsp;
          <a href="mailto:support@assistlist.ca">support@assistlist.ca</a>.
        </List.Item>
        <List.Item>
          Disable cookies through your browser settings.
        </List.Item>
        <List.Item>
          Opt out of certain Google advertising features using&nbsp;
          <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>.
        </List.Item>
      </List>

      <Header as="h3" content="10. Access, Correction and Deletion" />
      <p>
        You may contact us at <a href="mailto:support@assistlist.ca">support@assistlist.ca</a> to:
      </p>
      <List bulleted>
        <List.Item>
          Request access to personal information we hold about you;
        </List.Item>
        <List.Item>
          Request correction of inaccurate or incomplete personal information; or
        </List.Item>
        <List.Item>
          Request deletion of your account and personal information, subject to information we may
          need to retain for legal, security, fraud prevention, dispute resolution, or other
          legitimate purposes.
        </List.Item>
      </List>
      <p>We will take reasonable steps to verify your identity before processing a request.</p>

      <Header as="h3" content="11. Privacy and Security Incidents" />
      <p>
        We maintain reasonable administrative, technical and physical safeguards designed to protect
        personal information against unauthorized access, collection, use, disclosure, loss or
        destruction.
      </p>
      <p>
        If we become aware of a privacy or security incident involving personal information, we will
        take reasonable steps to contain and investigate the incident, assess the potential risk of
        harm and take appropriate measures to reduce or prevent further harm.
      </p>
      <p>
        Where notification is required or appropriate, we will notify affected individuals and/or
        relevant authorities as required by applicable law.
      </p>

      <Header as="h3" content="12. Changes to This Privacy Policy" />
      <p>We may update this Privacy Policy from time to time.</p>
      <p>
        The most current version will always be posted on our website. Continued use of AssistList
        after changes are posted constitutes acceptance of the revised Privacy Policy.
      </p>

      <Header as="h3" content="13. Privacy Contact" />
      <p>
        AssistList has designated a Privacy Officer to oversee compliance with applicable privacy
        laws. If you have questions about our privacy practices, wish to request access to or
        correction of your personal information or have a privacy concern, please contact us
        at:&nbsp;
        <a href="mailto:support@assistlist.ca">support@assistlist.ca</a>
      </p>
      <p>We will take reasonable steps to verify your identity before processing a request.</p>
      <p>
        If you have a privacy concern that we have not resolved to your satisfaction, you may also
        contact the&nbsp;
        <a href="https://oipc.bc.ca" target="_blank" rel="noopener noreferrer">
          Office of the Information and Privacy Commissioner for British Columbia
        </a>.
      </p>
      <p>
        <a href="#policies-top">Back to top</a>
      </p>
    </Segment>

    <Segment>
      <Header as="h1" id="contact-us" content="Contact Us" />
      <p>
        If you have any questions about these Terms, the Marketplace Rules, or our Services, please
        send us a message on our <Link to="/aboutus/contactus">Contact Form</Link>.
      </p>
      <p>
        We are a volunteer-run charitable organization and will do our best to respond as promptly
        as possible.
      </p>
      <p>
        <a href="#policies-top">Back to top</a>
      </p>
    </Segment>
  </PageContainer>
);

export default Policies;
